import React, { useState } from 'react';
import {
  Flex,
  GrayBackground,
  Page,
  Padding
} from 'components/base/layout-components';
import Table from 'components/base/Table';
import { getColor } from 'common/constants/_colors';
import Text from 'components/base/Text';
import styled from 'styled-components';
import { SPACES } from 'common/constants/sizes';
import PulseSkeleton from 'images/pulse-skeleton.svg';
import IterationTimeline from './components/IterationGraph/IterationTimeline/IterationTimeline';
import { getTimelineData } from './pulse.service';
import PulseTopBar from './components/PulseTopBar';
import PulsePeopleFilter from './components/PulsePeopleFilter';
import NotConnectedState from 'components/NotConnectedState';
import EmptyData, { FilteredPersonNonIssues } from 'components/EmptyData';
import { MAX_ISSUES_FOR_TOGGLE_UNMATCHED } from './pulse.common';
import FiltersInfo from './components/FiltersInfo';
import UnMatchedBranches from './components/UnMatchedBranches/UnMatchedBranches';
import withPulseData from './withPulseData';
import withErrorBoundary from 'components/HOC/ErrorBoundryHOC';
import usePmViewer from 'common/hooks/pm/usePmViewer';
import { upperCase } from 'lodash';
import usePulseEmptyStateConfig from './team-space/usePulseEmptyStateConfig';

const StyledPage = styled(Page)`
  padding: ${SPACES.small};
`;

const StyledBackground = styled(GrayBackground)`
  overflow: hidden;
`;

const FiltersBox = styled(Flex)`
  background-color: ${getColor('white')};
  border-radius: 2px;
`;

const TableWrapper = styled(Flex)`
  flex-direction: column;
  ${({ unmatchedBranches }) => (unmatchedBranches ? '' : 'height: 100%;')}
  width: 100%;
  background-color: ${getColor('white')};
  padding-top: 1rem;
  .MuiTableRow-head {
    border-top: 0 !important;
  }
`;

const StuckHeader = styled(Text)`
  color: ${getColor('gray')};
  letter-spacing: 0.01071em;
`;

const Pulse = props => {
  const {
    iterations,
    currentTeam,
    pmSensor,
    data,
    filteredData,
    filteredDataWithoutContributors,
    tableHeight,
    workingDays,
    branchesData,
    allowAnimations,
    isLoading,
    selectedContributorIds,
    peopleFilterData,
    topBarData,
    latestIteration,
    notConnectedType,
    issueStateFilterValues,
    setSelectedContributorIds,
    setAllowAnimations,
    setTableHeight,
    onSearch,
    issueTypeFilterValues,
    searchFilterInitValue,
    setIssueTypeFilterValues,
    setIssueStateFilterValues,
    contributorsById,
    contributors,
    myAccount,
    showFilterPeopleWithNoIssues,
    showEmptyState,
    showTable,
    showNoIssues,
    pulseActiveBranches
  } = props;

  const [unmatchedBranchesOpen, setUnmatchedBranchesOpen] = useState(false);
  const [scrollIntoUnmatchedBranches, setScrollIntoUnmatchedBranches] =
    useState(false);
  const pmViewer = usePmViewer();
  const emptyStateConfig = usePulseEmptyStateConfig(notConnectedType);

  const columns = [
    {
      name: 'ISSUES',
      dataPoint: 'issues',
      tableCellProps: {
        style: {
          width: '45%',
          minWidth: '30rem'
        }
      },
      ColumnHeaderComponent: () => (
        <Flex items={'baseline'}>
          <Text pl='1rem' size='large' color='shuttleGray'>{`
          LATEST ${filteredData.length || 0} ${upperCase(
            pmViewer.ticket.plural
          )}`}</Text>
          <Text pl='1rem' color='shuttleGray'>
            last 14 days
          </Text>
          <Text pl='0.5rem' size='large' color='botticelli'>
            {' '}
            |{' '}
          </Text>
          <Text pl='0.5rem' color='shuttleGray'>
            Sorted by: Recent activity
          </Text>
          <FiltersInfo
            issueStateFilterValues={issueStateFilterValues}
            issueTypeFilterValues={issueTypeFilterValues}
            topBarIssueTypes={topBarData.issueTypes}
          />
        </Flex>
      )
    },
    {
      name: 'MEMBERS',
      dataPoint: 'contributors',
      tableCellProps: {
        style: {
          width: '15%',
          minWidth: '10rem'
        }
      }
    },
    {
      name: 'PR SIZE',
      dataPoint: 'codeChanges',
      tableCellProps: {
        style: {
          width: '15%',
          minWidth: '10rem'
        }
      }
    },
    {
      name: 'BRANCHES',
      dataPoint: 'activity',
      tableCellProps: {
        style: {
          width: '15%',
          minWidth: '10rem'
        }
      }
    },
    {
      name: '',
      dataPoint: 'timeline',
      tableCellProps: {
        style: {
          width: '45rem',
          padding: 0
        }
      },
      ColumnHeaderComponent: () => (
        <IterationTimeline
          tableHeight={tableHeight}
          {...getTimelineData(iterations)}
          workingDays={workingDays}
        />
      )
    },
    {
      name: '',
      dataPoint: 'stuck',
      tableCellProps: {
        style: {
          width: '10%',
          minWidth: '10rem',
          padding: '0.8rem 0'
        }
      },
      ColumnHeaderComponent: () => (
        <StuckHeader pl='1.2rem' pr='1.2rem'>
          ALERTS
        </StuckHeader>
      )
    }
  ];

  if (notConnectedType) {
    return (
      <StyledPage>
        <StyledBackground>
          <NotConnectedState {...emptyStateConfig} />
        </StyledBackground>
      </StyledPage>
    );
  }

  return (
    <StyledPage>
      <StyledBackground>
        <FiltersBox direction='column' mb='small'>
          <PulseTopBar
            teamName={currentTeam.name}
            iteration={latestIteration}
            issueStateFilters={issueStateFilterValues}
            setIssueStateFilters={values => {
              setAllowAnimations(false);
              setIssueStateFilterValues(values);
            }}
            setIssueTypeFilters={values => {
              setAllowAnimations(false);
              setIssueTypeFilterValues(values);
            }}
            searchFilterValue={searchFilterInitValue}
            setSearchFilterValue={onSearch}
            allBranches={pulseActiveBranches}
            unmatchedBranches={branchesData.unmatched.length}
            allowFilterAnimations={allowAnimations}
            sensor_id={pmSensor.sensor_id}
            setUnmatchedBranchesOpen={setUnmatchedBranchesOpen}
            setScrollIntoUnmatchedBranches={setScrollIntoUnmatchedBranches}
            {...topBarData}
          />
          <PulsePeopleFilter
            filteredDataLength={filteredData.length}
            issuesDataLength={filteredDataWithoutContributors.length}
            data={peopleFilterData}
            teamId={currentTeam.id}
            teamName={currentTeam.name}
            selectedContributorId={selectedContributorIds}
            setSelectedContributorId={values => {
              setAllowAnimations(false);
              setSelectedContributorIds(values);
            }}
          />
          {showFilterPeopleWithNoIssues || showNoIssues ? (
            <FilteredPersonNonIssues
              title={
                showFilterPeopleWithNoIssues
                  ? `${
                      contributorsById[selectedContributorIds[0]].name
                    } is not working on any branches that are linked to ${
                      pmViewer.ticket.plural
                    }`
                  : `No ${pmViewer.ticket.plural} were found under the filters selected, but here are some branches:`
              }
            />
          ) : null}
        </FiltersBox>
        {showEmptyState && (
          <TableWrapper>
            <EmptyData
              title={data.length ? 'All filtered out' : 'Nothing here yet'}
              info={
                data.length
                  ? `Try changing the filters to see some ${pmViewer.ticket.plural}`
                  : `Looks like you have no ${pmViewer.ticket.plural}`
              }
            />
          </TableWrapper>
        )}
        {showTable && (
          <TableWrapper unmatchedBranches={branchesData.unmatched.length > 0}>
            {isLoading && (
              <img src={PulseSkeleton} width='100%' alt='Loading...' />
            )}
            {!isLoading && !!data.length && !!filteredData.length && (
              <Table
                lineHighlightOnHover
                data={filteredData}
                columns={columns}
                tableComponentProps={{
                  ref: el =>
                    setTableHeight(
                      `${el ? el.getBoundingClientRect().height : 0}px`
                    )
                }}
                transitionProps={{ enabled: allowAnimations, rowKey: 'key' }}
              />
            )}
          </TableWrapper>
        )}
        <Padding t={showTable ? 'small' : ''}>
          <UnMatchedBranches
            tableHeight={tableHeight}
            iterations={iterations}
            workingDays={workingDays}
            data={branchesData.unmatched}
            selectedContributorIds={selectedContributorIds}
            contributorsById={contributorsById}
            contributors={contributors}
            myAccount={myAccount}
            isOpen={
              filteredData.length < MAX_ISSUES_FOR_TOGGLE_UNMATCHED ||
              unmatchedBranchesOpen
            }
            setIsOpen={setUnmatchedBranchesOpen}
            scrollIntoUnmatchedBranches={scrollIntoUnmatchedBranches}
            setScrollIntoUnmatchedBranches={setScrollIntoUnmatchedBranches}
          />
        </Padding>
      </StyledBackground>
    </StyledPage>
  );
};

export default withPulseData(withErrorBoundary(Pulse, 'Pulse'));
